import type { Vec2 } from './types';

// Small 2D helpers. Math.hypot is avoided on purpose: it is slower and not guaranteed to round
// identically across engines, and the simulation should agree between server (V8) and browsers.

export function len(v: Vec2): number {
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

export function dist(a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function sub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(v: Vec2, k: number): Vec2 {
  return { x: v.x * k, y: v.y * k };
}

export function dot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}

/** Unit vector, or (0, 0) for a zero vector. */
export function normalize(v: Vec2): Vec2 {
  const l = len(v);
  return l > 0 ? { x: v.x / l, y: v.y / l } : { x: 0, y: 0 };
}

/** Same direction, length at most `max`. */
export function clampLen(v: Vec2, max: number): Vec2 {
  const l = len(v);
  return l > max ? { x: (v.x / l) * max, y: (v.y / l) * max } : v;
}

/** Replaces NaN/Infinity by 0 so a hostile or broken input cannot poison the state. */
export function sanitize(v: Vec2 | undefined): Vec2 {
  if (!v) return { x: 0, y: 0 };
  return { x: Number.isFinite(v.x) ? v.x : 0, y: Number.isFinite(v.y) ? v.y : 0 };
}

/** Rotates a vector by `rad` (positive = clockwise on a y-down screen). */
export function rotate(v: Vec2, rad: number): Vec2 {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
}

/** Distance from `p` to the segment `a`–`b`. */
export function distToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const l2 = abx * abx + aby * aby;
  let t = 0;
  if (l2 > 0) t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / l2));
  return dist(p, { x: a.x + abx * t, y: a.y + aby * t });
}

/** Distance along `dir` (unit) from `from` to the first contact with a circle, or null if the ray misses it. */
export function rayCircle(from: Vec2, dir: Vec2, centre: Vec2, radius: number): number | null {
  const toC = sub(centre, from);
  const t = dot(toC, dir);
  if (t <= 0) return null;
  const d2 = dot(toC, toC) - t * t;
  const r2 = radius * radius;
  if (d2 > r2) return null;
  return Math.max(0, t - Math.sqrt(r2 - d2));
}
